import { Observable, of } from 'rxjs';
import { LegalEntity } from '@ms-organization-entities';
import { ApiClientUtils } from '../../api-client.utils';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';

import {
  CommonDocumentEditorDetails,
  DocRenameRequest,
  DocumentDuplicateResponse,
  DocumentLockResponse,
  ItemDocDependency
} from '@primatoo-ms-organization';
import { HttpClient } from '@angular/common/http';
import { TreatmentType } from 'it/primatoo/openapi/api/org/schemas/treatmentdoc/TreatmentType';
import { DownloadFormat, DownloadResponse } from '@pt/pt-models';
import { DownloadRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/DownloadRequest';
import { GetChapterResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/GetChapterResponse';
import {
  ListTemplateChapterResponse
} from 'it/primatoo/openapi/api/org/schemas/common/docs/ListTemplateChapterResponse';
import { GetTreatmentResultResponse } from 'it/primatoo/openapi/api/org/schemas/treatment/GetTreatmentResultResponse';
import { SetChapterStatusRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SetChapterStatusRequest';
import { ListUserColumnsResponse } from 'it/primatoo/openapi/api/org/schemas/common/ListUserColumnsResponse';
import { map } from 'rxjs/operators';
import { ValidateDocumentRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/ValidateDocumentRequest';
import { UpsertUserColumnRequest } from 'it/primatoo/openapi/api/org/schemas/common/UpsertUserColumnRequest';
import { CreateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/CreateDocResponse';
import { DocumentItem } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listdocumentresponse/DocumentItem';
import {
  FindAllValidatedDocResponse
} from 'it/primatoo/openapi/api/org/schemas/common/docs/FindAllValidatedDocResponse';
import { PatchValidatedRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/PatchValidatedRequest';
import {
  DisableTreatmentWarningRequest
} from 'it/primatoo/openapi/api/org/schemas/treatment/DisableTreatmentWarningRequest';
import { UpdateDocResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpdateDocResponse';
import {
  GetGlobalMeasCheckValidatedForDetailsResponse
} from 'it/primatoo/openapi/api/org/schemas/globalmeasurecheck/GetGlobalMeasCheckValidatedForDetailsResponse';
import { DeleteEntityServiceInterface } from './delete-entity-service-interface';
import { UpsertWidgetsRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/UpsertWidgetsRequest';
import * as FileSaver from 'file-saver';
import { SetChapterCompletedRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SetChapterCompletedRequest';

/**
 * Interfaccia comune a Treatment, Assessment, Dpia ....
 * Sostituire nell'implementazione any con lo specifico DTO
 */
export abstract class BusinessProjectManagementService implements DeleteEntityServiceInterface {

  protected constructor(public httpClient: HttpClient) {
  }

  abstract getDocUserPermission(docId: number): Observable<any>;

  abstract getActivePermission(docId: number): Observable<any>;

  abstract patchActivePermission(docId: number, request: any): Observable<void>;

  abstract deleteMultiple(ids: number[]): Observable<{ failed: number[] }>;

  abstract getDocColumn(type?: TreatmentType): Observable<ColumnWithLocalization[]>;

  abstract create(body: any): Observable<CreateDocResponse>;



  /**
   * da usare SOLO nella sezione dettagli documento (questa api cancella l'intero documento)
   * @param id
   */
  abstract delete(id: number): Observable<void>;

  abstract update(id: number, body: any): Observable<UpdateDocResponse>;

  abstract rename(id: number, request: DocRenameRequest): Observable<void>;

  abstract getToOverride(treatmentTypes?: TreatmentType): Observable<any>;

  abstract duplicate(id: number, request: any): Observable<DocumentDuplicateResponse>;

  abstract reopen(id: number): Observable<void>;


  public getColumn(treatmentType?: TreatmentType): Observable<ColumnWithLocalization[]> {
    let url = this.getEndPointV2() + `columns/`;
    if (treatmentType) {
      url += `type/${treatmentType}/`;
    }
    return this.httpClient.get<ListUserColumnsResponse>(url).pipe(map(c => c.columns));
  }


  public setColumns(request: UpsertUserColumnRequest, treatmentTypes?: TreatmentType): Observable<void> {
    let url = this.getEndPointV2() + `user-columns/`;
    if (treatmentTypes) {
      url += `type/${treatmentTypes}/`;
    }
    return this.httpClient.post<void>(url, request);
  }

  abstract getDetails(id: number): Observable<any>;

  abstract getEditorDetails(id: number, version?: number): Observable<CommonDocumentEditorDetails>;

  abstract upsertWidgets(id: number, widgets: UpsertWidgetsRequest): Observable<void>;

  abstract preview(id: number, request?: DownloadRequest);

  abstract getTreatmentTemplateChapterId(id: number, chapterId: number): Observable<GetChapterResponse>;

  abstract getDocumentTemplateChapters(id: number): Observable<ListTemplateChapterResponse>;

  abstract lockResource(id: number, token: number): Observable<DocumentLockResponse>;

  abstract releaseLock(id: number): Observable<any>;

  abstract publish(id: number): Observable<void>;

  // abstract isValidable(id: number): Observable<IsValidableResponse>;

  abstract getEndPointV1(): string;

  abstract getEndPointV2(): string;

  abstract refreshResults(id: number): Observable<any>;

  abstract userChecked(id: number): Observable<any>;

  // abstract findAll(selectedLegalEntities?: LegalEntity[], treatmentType?: TreatmentType): Observable<any[]>;

  abstract findAllFiltered(filter?: string, filterByAnyPermission?: boolean,
                           columns?: string[],
                           selectedLegalEntities?: LegalEntity[],
                           treatmentType?: TreatmentType): Observable<Array<DocumentItem>> ;

  public getDocumentValidated(docId: number): Observable<FindAllValidatedDocResponse> {
    const url = this.getEndPointV2() + `${docId}/validated/`;
    return this.httpClient.get<FindAllValidatedDocResponse>(url);
  }


  public snapshot(docId: number, request: ValidateDocumentRequest): Observable<void> {
    const url = this.getEndPointV2() + `${docId}/latest/snapshot`;
    return this.httpClient.post<void>(url, request);
  }

  public validate(docId: number, request: ValidateDocumentRequest): Observable<void> {
    const url = this.getEndPointV2() + `${docId}/latest/validate`;
    return this.httpClient.post<void>(url, request);
  }

  public setChapterStatus(docId: number, chapterId: number, request: SetChapterStatusRequest): Observable<void> {
    const url = this.getEndPointV2() + `${docId}/latest/template/chapters/${chapterId}/status/`;
    return this.httpClient.patch<void>(url, request);
  }


  public getResult(docId: number): Observable<GetTreatmentResultResponse> {
    const url = this.getEndPointV2() + `${docId}/latest/results/`;
    return this.httpClient.get<GetTreatmentResultResponse>(url);
  }

  public getRefreshResult(docId: number): Observable<void> {
    const url = this.getEndPointV1() + `${docId}/latest/refresh-results`;
    return this.httpClient.post<void>(url, {});
  }

  public reindex(): Observable<void> {
    const url = this.getEndPointV2() + `reindex`;
    return this.httpClient.post<void>(url, {});
  }

  public downloadVersionPdfUrl(id: number): string {
    return this.getEndPointV2() + `${id}/latest/download/pdf`;
  }

  public downloadPreviewPdfUrl(resourceId: number): string {
    return this.getEndPointV2() + `${resourceId}/latest/download/pdf`;
  }

  public downloadPreviewPdf(id: number, request?: DownloadRequest, fileName?: string): Observable<DownloadResponse> {
    const url = this.downloadPreviewPdfUrl(id);
    return ApiClientUtils.downloadChapter(this.httpClient, url, request, DownloadFormat.PDF, fileName);
  }
  public downloadPreviewWordUrl(resourceId: number): string {
    return this.getEndPointV2() + `${resourceId}/latest/download/word`;
  }
  public downloadWord(id: number, request?: DownloadRequest, fileName?: string): Observable<void> {
    const url = this.downloadPreviewWordUrl(id);
    const format = DownloadFormat.DOC;
    const defaultFileName = (fileName ?? 'document') + '.docx';

    return ApiClientUtils.downloadChapter(this.httpClient, url, request, format, fileName).pipe(
      map((response) => {
        if (response) {
          FileSaver.saveAs(response.blob, defaultFileName);
        }
      })
    );
  }

  /**
   * VALIDATED
   */

  deleteValidated(docId: number, validatedId: number): Observable<void> {
    const url = this.getEndPointV2() + `${docId}/validated/${validatedId}/`;
    return this.httpClient.delete<void>(url);
  }

  public downloadValidated(validatedId: number, fileName?: string): Observable<DownloadResponse> {
    const url = this.getEndPointV2() + `validated/${validatedId}/download`;
    return ApiClientUtils.download(this.httpClient, url, DownloadFormat.PDF, fileName);
  }

  public renameDocumentValidated(docId: number, validatedId: number, request: PatchValidatedRequest): Observable<void> {
    const url = this.getEndPointV2() + `${docId}/validated/${validatedId}/`;
    return this.httpClient.patch<void>(url, request);
  }

  public getDocumentValidatedDetails(docId: number, validatedId: number): Observable<GetGlobalMeasCheckValidatedForDetailsResponse> {
    const url = this.getEndPointV2() + `${docId}/validated/${validatedId}/`;
    return this.httpClient.get<GetGlobalMeasCheckValidatedForDetailsResponse>(url);
  }


  public completed(docId: number, chapterId: number, request: SetChapterCompletedRequest): Observable<void>  {
    const url = this.getEndPointV2() + `${docId}/latest/template/chapters/${chapterId}/completed/`;
    return this.httpClient.patch<void>(url, request);
  }

  /**
   *
   * RID_CHANGED:
   * Qualcuno ha cambiato i RID di un applicativo presente nel trattamento, quindi nel capitolo
   * RIsk Assessment ci sono i warning nel widget APP_SUMMARY
   *
   * GLOBAL_MEAS_ANSWER_CHANGED:
   * Qualcuno ha cambiato le risponde delle misure globali presenti nel documento
   * global-measure-check selezionato nel widget risk_Assessment del trattamento.
   * Quindi nel capitolo RIsk Assessment ci sono i warning nel widget APP_SUMMARY
   *
   * APP_LIST_CHANGED:
   * qualcuno ha cambiato nella checkbox degli applicativi la lista.
   * Quindi nel widget app summary ci vuole un warning per allineare e portare le nuove/eliminate apps
   *
   *
   * GLOBAL_MEAS_LIST_CHANGED:
   * qualcuno ha cambiato nella checkbox delle misure globali la lista.
   * Quindi nel widget risk assessment delle globali ci vuole un warning
   * per allineare e portare le nuove/eliminate misure
   *
   * @param docId
   * @param request
   */
  disableWarnings(docId: number, request: DisableTreatmentWarningRequest): Observable<void> {
    const url = this.getEndPointV2() + `${docId}/disable-warnings`;
    return this.httpClient.post<void>(url, request);
  }


  getDocDependencies(id: number): Observable<ItemDocDependency[]> {
    return of([]);
  }

}
